// BiasCheckSettings — the bias check (Nilay Sharma's proposal pr_81802f2874):
// the four-fifths rule on the explained CV score, per vacancy pool, for age
// band, gender, category, religion and marital status. Groups only, never a
// person; read-only. And the gate: no score decides who goes on unless the
// CHRM's written sign-off is on record (GET /api/cv-scoring/bias-check,
// POST …/run, PUT …/gate). A recruitment administrator only.
// @story HIR-GL2

import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { Button, Input, StatusPill } from '../../ds';
import { Loading, Note } from '../../components/page';

const cell = { padding: '4px 8px', borderBottom: '1px solid var(--border)', fontSize: 'var(--fs-body-sm)', textAlign: 'left' };

export default function BiasCheckSettings() {
  const [st, setSt] = useState({ phase: 'loading' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [sign, setSign] = useState({ chrmName: '', text: '', acknowledgesFlags: false });

  const load = () => api.get('/cv-scoring/bias-check')
    .then((r) => setSt({ phase: 'ready', ...r.data.data }))
    .catch((e) => setSt({ phase: e?.response?.status === 403 ? 'forbidden' : 'error' }));
  useEffect(() => { load(); }, []);

  const run = () => {
    setBusy(true); setMsg(null);
    api.post('/cv-scoring/bias-check/run', {})
      .then(() => load())
      .catch((e) => setMsg({ tone: 'danger', text: e?.response?.data?.message || 'Could not run the check.' }))
      .finally(() => setBusy(false));
  };
  const gate = (scoresMayGate) => {
    setMsg(null);
    api.put('/cv-scoring/bias-check/gate', { scoresMayGate, ...(scoresMayGate ? { signOff: sign } : {}) })
      .then(() => { setMsg({ tone: 'success', text: scoresMayGate ? 'Scores may now gate progression, on the CHRM\'s sign-off.' : 'No score gates progression.' }); load(); })
      .catch((e) => setMsg({ tone: 'danger', text: e?.response?.data?.message || 'Could not change the gate.' }));
  };

  if (st.phase === 'loading') return <Loading label="Reading the bias check" />;
  if (st.phase === 'forbidden') return <Note tone="info">The bias check is for a recruitment administrator.</Note>;
  if (st.phase === 'error') return <Note tone="warning">The bias check could not be read just now. Reload the page to try again.</Note>;
  const last = st.latest;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <p style={{ margin: 0, color: 'var(--text-2)' }}>
        Whether the explained CV score shortlists some groups of candidates less than others (the four-fifths rule, at a 10% shortlist).
        A group is rated only with 25 people and 5 expected selections. It shows groups, never a person, and changes nothing.
      </p>
      <div><Button onClick={run} disabled={busy}>{busy ? 'Running…' : 'Run the bias check'}</Button></div>
      {!last ? <Note tone="info">The check has not run yet.</Note> : (
        <div style={{ display: 'grid', gap: 12 }}>
          <div style={{ fontSize: 'var(--fs-body-sm)' }}>
            Last run {new Date(last.ranAt).toLocaleString('en-IN')} {last.by?.name ? `by ${last.by.name}` : ''} · score {last.method?.score}{' '}
            <StatusPill tone={last.flagged ? 'danger' : 'success'}>{last.flagged ? 'A group is flagged' : 'Nothing flagged'}</StatusPill>
          </div>
          {(last.pools || []).map((p) => (
            <details key={p.vacancyId} open={p.flagged}>
              <summary style={{ cursor: 'pointer', fontSize: 'var(--fs-body-sm)' }}>{p.role}: {p.pool} candidates, top {p.shortlisted}{p.flagged ? ' — flagged' : ''}</summary>
              {p.dimensions.map((d) => (
                <table key={d.dimension} style={{ borderCollapse: 'collapse', width: '100%', marginTop: 6 }}>
                  <thead><tr><th style={cell}>{d.dimension}</th><th style={cell}>People</th><th style={cell}>Shortlisted</th><th style={cell}>Ratio</th><th style={cell} /></tr></thead>
                  <tbody>
                    {d.groups.map((g) => (
                      <tr key={g.group}>
                        <td style={cell}>{g.group}</td>
                        <td style={cell}>{g.people}</td>
                        <td style={cell}>{g.selected}</td>
                        <td style={cell}>{g.impactRatio == null ? '—' : g.impactRatio}</td>
                        <td style={cell}>{g.flagged ? `Flagged: most behind on ${g.explainedBy?.component || 'a component'}` : (g.why || '')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ))}
            </details>
          ))}
        </div>
      )}

      <div style={{ display: 'grid', gap: 8, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
        <strong>May a score decide who goes on?</strong>
        {st.gate?.scoresMayGate
          ? <Note tone="warning">Yes, on {st.gate.signOff?.chrmName}'s sign-off of {new Date(st.gate.signOff?.at).toLocaleDateString('en-IN')}: "{st.gate.signOff?.text}"</Note>
          : <Note tone="info">No. No score gates progression. Opening this needs the CHRM's written sign-off.</Note>}
        {st.gate?.scoresMayGate ? <div><Button variant="secondary" onClick={() => gate(false)}>Stop scores gating progression</Button></div> : (
          <>
            <Input label="CHRM's name" value={sign.chrmName} onChange={(e) => setSign((s) => ({ ...s, chrmName: e.target.value }))} />
            <label style={{ display: 'grid', gap: 6, fontSize: 'var(--fs-body-sm)' }}>
              The CHRM's sign-off, in their words
              <textarea aria-label="Sign-off" rows={3} value={sign.text} onChange={(e) => setSign((s) => ({ ...s, text: e.target.value }))}
                style={{ width: '100%', border: '1px solid var(--border)', borderRadius: 6, padding: 8, font: 'inherit' }} />
            </label>
            {last?.flagged && (
              <label style={{ fontSize: 'var(--fs-body-sm)', display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="checkbox" checked={sign.acknowledgesFlags} onChange={(e) => setSign((s) => ({ ...s, acknowledgesFlags: e.target.checked }))} />
                The sign-off says, in writing, that it covers the flagged groups
              </label>
            )}
            <div><Button variant="secondary" onClick={() => gate(true)}>Record the sign-off and let scores gate</Button></div>
          </>
        )}
      </div>
      {msg && <Note tone={msg.tone}>{msg.text}</Note>}
    </div>
  );
}
